import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { parentNotificationApi } from '@/services/api/parent';
import type { NotificationPreferences } from '@/services/api/parent';

/**
 * Syncs the parent's language preference between browser and server.
 *
 * Two modes based on `languageSetByUser`:
 * - true:  Server is authoritative -> sync server language to i18next
 * - false: Browser is authoritative -> push browser/toggle language to server
 *
 * This ensures existing parents (who never set a language) get their
 * browser-detected language saved on first login, while parents who
 * explicitly chose a language always have it respected.
 */
export function useParentLanguage() {
  const { i18n } = useTranslation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const hasPushedRef = useRef(false);

  const { data: preferences } = useQuery({
    queryKey: ['parent-notification-preferences'],
    queryFn: async () => {
      const response = await parentNotificationApi.getPreferences();
      return response.data as NotificationPreferences;
    },
    enabled: user?.role === 'parent',
    staleTime: 5 * 60 * 1000,
  });

  const pushLanguageMutation = useMutation({
    mutationFn: (language: 'en' | 'es') =>
      parentNotificationApi.updatePreferences({ language }),
    onSuccess: (response) => {
      queryClient.setQueryData(['parent-notification-preferences'], response.data);
    },
  });

  // Stable ref to mutate — avoids putting the unstable useMutation return in effect deps
  const mutateRef = useRef(pushLanguageMutation.mutate);
  mutateRef.current = pushLanguageMutation.mutate;

  useEffect(() => {
    if (!preferences) return;

    if (preferences.languageSetByUser) {
      // Server is authoritative — sync server -> frontend
      if (preferences.language && preferences.language !== i18n.language) {
        i18n.changeLanguage(preferences.language);
      }
    } else if (!hasPushedRef.current) {
      // Browser is authoritative — push frontend -> server (once per mount).
      // Set ref before calling mutate to guard against re-runs if preferences
      // refetches while the mutation is in-flight.
      hasPushedRef.current = true;
      const currentLang = (i18n.language?.startsWith('es') ? 'es' : 'en') as 'en' | 'es';
      mutateRef.current(currentLang);
    }
  }, [preferences, i18n]);

  return { language: preferences?.language || i18n.language };
}
